import { SlideHeader } from "../../../SlideHeader";

// Zone 1 is a diagram, not a card row (ai-dev/docs/diagrams/architecture-diagram-plan.md
// #2a). What the five cards could not show, and the drawing does: the human step is
// only the first one, and the 23 gates span everything to the right of it.
//
// The drawing is a standalone .svg under public/, not inline JSX — one file, editable
// as a drawing rather than as markup, and it is the single source for this figure
// wherever else it is used. It is authored at the slide's own content width
// (1280 - pl-24 - pr-20 = 1104) and carries intrinsic width/height, so `w-full`
// scales it without a layout shift. Colours are baked into the file, so the print
// path needs nothing from `print-color-adjust`.
//
// It lives under /img/ rather than /era/ deliberately: /era/* is the deck's
// own SPA route, and an asset path that shadows a route is a fight waiting to happen.
// Public assets live under Vite's base ("/admin/") in dev and prod — the
// worker's /admin branch strips the base and hits ASSETS. A bare "/img/…"
// path 404s in dev and renders as a broken image (fixed 2026-09-05).
const PIPELINE_DIAGRAM = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/img/diagrams/ticket-to-customer-pipeline.svg`;

const SESSIONS: Array<{ name: string; cadence: string; detail: string }> = [
  {
    name: "Build",
    cadence: "continuous",
    detail:
      "Reacts to the ticket itself, not to a clock: selects ready tickets, implements them with agent teams, verifies, and lands on the release branch.",
  },
  {
    name: "Detect & repair",
    cadence: "nightly",
    detail:
      "Reads production telemetry — error logs, delivery tripwires, cost-burst monitors — triages, files tickets with evidence, then fixes the serious ones itself the same night.",
  },
  {
    name: "Ship",
    cadence: "nightly",
    detail:
      "Re-verifies the release from scratch, confirms staging passed its test suites, then triggers and monitors the production deploy. No human in the loop.",
  },
];

const STATS: Array<{ value: string; label: string }> = [
  { value: "100%", label: "of code AI-authored — inside a factory the founder engineered and oversees" },
  { value: "1,565", label: "PRs merged in the last 90 days" },
  { value: "nightly", label: "autonomous production deploys" },
  { value: "23", label: "automated gates from code to prod" },
  { value: "3–6", label: "agents on each ticket team" },
  { value: "1.3M", label: "lines of code maintained" },
];

export default function AiNativeFactory() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-10 md:[&_header]:mb-5">
      <SlideHeader tab unnumbered eyebrow="Appendix E · Agentic Software Factory">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          An engineering org{" "}
          <span className="text-blue-700">made of AI agents</span> — run by one
          founder.
        </h1>
        <p className="mt-3 text-lg font-medium italic tracking-tight text-slate-700 md:text-xl">
          Agent teams plan, build, and adversarially test every ticket;
          scheduled sessions repair production and ship nightly — unattended.
          The factory itself is the founder's engineering.
        </p>
      </SlideHeader>

      {/* The pipeline — ticket to production */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-sm">
          Every ticket travels the same pipeline
        </p>
        {/* Scrolls sideways on a phone rather than forking into a second mobile
            layout — a duplicate card list would be another copy of the same five
            claims to keep in sync. */}
        <div className="mt-2 overflow-x-auto">
          <img
            src={PIPELINE_DIAGRAM}
            alt="Five stages from ticket to production. Only the first stage involves a human; 23 automated gates span the remaining four."
            className="h-auto w-full min-w-[900px]"
          />
        </div>
      </div>

      {/* The three scheduled sessions */}
      <div className="mt-4 md:mt-5">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-sm">
          Three unattended sessions run the org — on hardware we own outright
        </p>
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-3">
          {SESSIONS.map((s) => (
            <div
              key={s.name}
              className="rounded-lg bg-blue-50/50 p-3 ring-1 ring-blue-200 md:p-4"
            >
              <p className="text-sm font-bold tracking-tight text-slate-900 md:text-base">
                {s.name}
                <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
                  {s.cadence}
                </span>
              </p>
              <p className="mt-1 text-xs leading-snug text-slate-700 md:text-sm">
                {s.detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Numbers */}
      <div className="mt-4 grid grid-cols-3 gap-2 md:mt-5 md:grid-cols-6 md:gap-3">
        {STATS.map((s) => (
          <div
            key={s.label}
            className="rounded-lg bg-slate-50 p-2.5 ring-1 ring-slate-200 md:p-3"
          >
            <p className="text-lg font-bold tracking-tight text-slate-900 md:text-xl">
              {s.value}
            </p>
            <p className="mt-0.5 text-xs leading-snug text-slate-600">
              {s.label}
            </p>
          </div>
        ))}
      </div>

      {/* The thesis, operating */}
      <div className="mt-4 rounded-xl bg-blue-50 px-5 py-3.5 ring-1 ring-blue-200 md:mt-auto md:px-6 md:py-4">
        <p className="text-sm leading-snug text-slate-800 md:text-base">
          <span className="font-semibold text-blue-900">
            “Coding is largely solved.”
          </span>{" "}
          — Boris Cherny, creator of Claude Code (Feb 2026).{" "}
          <span className="text-slate-700">
            Apptonomy is that thesis operating as a company — the build-cost
            counterfactual is Appendix C ($22–25M, 26 FTE, pre-AI); the spend
            consequence is slide 19’s 9%.
          </span>
        </p>
        <p className="mt-2 border-t border-blue-200 pt-2 text-xs leading-snug text-slate-600 md:text-sm">
          <span className="font-semibold text-slate-800">
            The category, and where we sit in it:
          </span>{" "}
          an <span className="font-medium text-slate-800">agentic software
          factory</span> is the 2026 term of art for a system where agents
          build, test and ship while humans set intent and review outcomes. The
          vendors selling one — Factory, Augment — sell a{" "}
          <span className="font-medium text-slate-800">multiplier</span> for an
          engineering org you already staff, and report 3–5×. Ours is a{" "}
          <span className="font-medium text-slate-800">substitute</span> for
          that org: it is why engineering is ~17% of the operating plan — one salary
          plus tooling — and not 40%.{" "}
          <span className="font-medium text-slate-800">
            And it is sector-agnostic
          </span>{" "}
          — the platform it built knows mobile app marketing; the factory
          itself does not. It is the repeatable half of the arc: every next
          wedge, and any future platform, ships through the same machine.
        </p>
      </div>
    </div>
  );
}
